import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { CompanyCityModel } from 'src/app/models/management/company-city.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CompanyCityTableModel } from 'src/app/models/tables/management/company-city-table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { CompanyCityService } from 'src/app/services/management/company-city.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { RegisterCompanyCityComponent } from '../register-company-city/register-company-city.component';

@Component({
  selector: 'app-company-city',
  templateUrl: './company-city.component.html',
  styleUrls: ['./company-city.component.scss'],
  standalone: false
})
export class CompanyCityComponent implements OnInit {

  @ViewChild('registerModal') registerModalComponent?: CustomModalComponent | null = null;

  ascendant: boolean = true;
  columnSort: string | null = null;
  companyCityModel: CompanyCityModel = new CompanyCityModel();
  componentRegisterCompanyCity = RegisterCompanyCityComponent;
  data: PagedResponseModel<CompanyCityTableModel> = new PagedResponseModel<CompanyCityTableModel>();
  filters: ColumnResponseModel[] = [];
  header: ColumnResponseModel[] = [];
  inputsForRegisterModals$ = new BehaviorSubject<any>({
    companyCityModel: new CompanyCityModel(),
    permissionUpdatable: false
  });
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Códigos Ciudades';
  output$ = new Subject<any>();
  page: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  subscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }, {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    },
  ];
  url: string = 'company-city';

  constructor(private confirmDialogService: ConfirmDialogService,
    private companyCityService: CompanyCityService,
    private headerService: HeaderService,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(`${this.url}`);
    if (filters) this.filters = JSON.parse(filters);
    this.getPermission();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllCompanyCitiesWithPagination(),
    ]);
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onReload':
          this.onReload(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async deleteCompanyCity(companyCity: CompanyCityTableModel) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para eliminar códigos ciudad.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el código de la ciudad?')) {
      try {
        await firstValueFrom(this.companyCityService.deleteCompanyCity(companyCity.companyCityId));
        await this.getAllCompanyCitiesWithPagination();
        this.toastHandlerService.showToast('success', 'El código de la ciudad fue eliminado exitosamente.');
      } catch (error) {
        console.error('Error al eliminar el código de la ciudad.', error);
      }
    }
  }

  async exportGeneralConsultation() {
    try {
      const response = await firstValueFrom(this.companyCityService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Códigos Ciudades.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar los impuestos.', error);
    }
  }

  async getAllCompanyCitiesWithPagination() {
    try {
      const response = await firstValueFrom(this.companyCityService.getAllCompanyCitiesWithPagination(this.page, this.limit,
        this.columnSort, this.ascendant, this.filters));
      this.data = response;
    } catch (error) {
      console.error('Error al buscar los códigos ciudad.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.header.length === 0) {
        const response = await firstValueFrom(this.companyCityService.getColumnsForGeneralConsultation());
        this.header = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general de los códigos ciudad.', error);
    }
  }

  async getCompanyCityById(companyCityId: number) {
    try {
      const response = await firstValueFrom(this.companyCityService.getCompanyCityById(companyCityId));
      this.companyCityModel = response;
      this.openRegisterModal();
    } catch (error) {
      console.error('Error al obtener el código de la ciudad por id.', error);
    }
  }

  async getPermission() {
    await this.getPermissionProfileByUrl();
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  newRegister() {
    this.companyCityModel = new CompanyCityModel();
    this.openRegisterModal();
  }

  openRegisterModal() {
    this.inputsForRegisterModals$.next({
      companyCityModel: this.companyCityModel,
      permissionUpdatable: this.permissionUpdatable
    });
    this.registerModalComponent && this.registerModalComponent.show();
  }

  async onAction(event: { action: string, item?: CompanyCityTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      await this.getCompanyCityById(event.item.id);
    }

    if (event.action === 'delete' && event.item) {
      await this.deleteCompanyCity(event.item);
    }
  }

  async onExport() {
    await this.exportGeneralConsultation();
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number; sortField?: string; sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField ?? null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllCompanyCitiesWithPagination();
  }

  async onReload(reload: boolean) {
    if (reload) {
      await this.getAllCompanyCitiesWithPagination();
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
